import { useEffect, useRef, useState } from "react";
import { Sparkles, Upload, Trash2, Loader2, Wand2, RefreshCw, Image as ImgIcon, RotateCcw } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Navigate } from "react-router-dom";

const SIZES = [
  { v: "1280x720", label: "Landscape HD (16:9)" },
  { v: "1792x1024", label: "Wide" },
  { v: "1024x1792", label: "Portrait" },
  { v: "1024x1024", label: "Square" },
];
const DURATIONS = [4, 8, 12];

export default function AdminLandingVideo() {
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [prompt, setPrompt] = useState("A bright animated montage: a hand placing a phone over a textbook page; vocabulary words gently lift off the page and slot themselves into a glowing notebook. Pastel yellow, mint and lavender palette. Cheerful, optimistic, modern flat-illustration style.");
  const [size, setSize] = useState("1280x720");
  const [duration, setDuration] = useState(4);
  const [model, setModel] = useState("sora-2");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  // ----- Hero image (poster) -----
  const [poster, setPoster] = useState(null);          // {url, is_default}
  const [posterDraft, setPosterDraft] = useState("");
  const [posterBusy, setPosterBusy] = useState(false);
  const loadPoster = async () => {
    try {
      const r = await api.get("/landing/poster");
      setPoster(r.data);
      setPosterDraft(r.data.is_default ? "" : (r.data.url || ""));
    } catch { /* ignore */ }
  };
  const savePoster = async () => {
    setPosterBusy(true);
    try {
      const r = await api.put("/admin/landing/poster", { url: posterDraft.trim() || null });
      setPoster(r.data);
      setPosterDraft(r.data.is_default ? "" : r.data.url);
      toast.success("Hero image updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update hero image.");
    } finally { setPosterBusy(false); }
  };
  const resetPoster = async () => {
    setPosterBusy(true);
    try {
      const r = await api.put("/admin/landing/poster", { url: null });
      setPoster(r.data);
      setPosterDraft("");
      toast.success("Reset to default");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't reset.");
    } finally { setPosterBusy(false); }
  };

  const load = async () => {
    try {
      const r = await api.get("/landing/video/admin");
      setInfo(r.data);
      return r.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load video info");
      return null;
    }
  };

  useEffect(() => {
    // Defer to next tick so React's set-state-in-effect lint stays happy
    // (load/loadPoster both setState inside their async bodies).
    const t = setTimeout(() => { load(); loadPoster(); }, 0);
    return () => {
      clearTimeout(t);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  if (user && user.is_admin === false) return <Navigate to="/dashboard" replace />;

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const d = await load();
      if (!d || d.status !== "generating") {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }, 8000);
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("video/")) { toast.error("Pick a video file"); return; }
    if (f.size > 50 * 1024 * 1024) { toast.error("Max 50 MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      await api.post("/landing/video/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Video uploaded");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const generate = async () => {
    if (!prompt.trim()) { toast.error("Prompt required"); return; }
    setBusy(true);
    try {
      await api.post("/landing/video/generate", { prompt, size, duration, model });
      toast.success("Generation started — this typically takes 2-5 minutes.");
      await load();
      startPolling();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start generation");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!confirm("Remove the current landing video?")) return;
    await api.delete("/landing/video");
    toast.success("Removed");
    load();
  };

  return (
    <div className="p-6 sm:p-8 lg:p-12 max-w-5xl">
      <header className="mb-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Admin</p>
        <h1 className="font-display text-4xl font-black mt-1">Landing visuals</h1>
        <p className="text-base font-medium text-slate-600 mt-2">
          Manage the hero image and optional video on the marketing landing page.
        </p>
      </header>

      {/* Hero image section */}
      <section className="nb-card p-6 mb-6" data-testid="hero-image-card">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="font-display text-2xl font-bold flex items-center gap-2">
            <ImgIcon className="w-5 h-5" strokeWidth={2.5}/> Hero image
          </h2>
          <button onClick={loadPoster} className="nb-btn-secondary text-xs inline-flex items-center gap-1 py-2 px-3" data-testid="hero-image-refresh">
            <RefreshCw className="w-3 h-3" strokeWidth={2.5}/> Refresh
          </button>
        </div>
        <p className="text-sm text-slate-600 mt-2">
          Shown on the landing page when no marketing video is set. Currently using{" "}
          {poster?.is_default ? <strong>the default Studict image</strong> : <strong>your custom URL</strong>}.
        </p>
        {poster?.url && (
          <div className="mt-3">
            <img
              src={poster.url}
              alt="Current landing hero"
              className="max-h-56 rounded-md border-2 border-slate-900"
              data-testid="hero-image-preview"
              onError={(e) => { e.currentTarget.style.opacity = "0.4"; }}
            />
          </div>
        )}
        <div className="mt-4 grid sm:grid-cols-[1fr_auto_auto] gap-2 items-end">
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-widest text-slate-700">Image URL</span>
            <input
              type="url"
              placeholder="https://… (paste any image link; leave blank to use the default)"
              value={posterDraft}
              onChange={(e) => setPosterDraft(e.target.value)}
              data-testid="hero-image-url-input"
              className="nb-input mt-1"
            />
          </label>
          <button onClick={savePoster} disabled={posterBusy} data-testid="hero-image-save" className="nb-btn-primary inline-flex items-center gap-2">
            {posterBusy ? "Saving…" : "Save"}
          </button>
          <button onClick={resetPoster} disabled={posterBusy || poster?.is_default} data-testid="hero-image-reset" className="nb-btn-secondary inline-flex items-center gap-2">
            <RotateCcw className="w-4 h-4" strokeWidth={2.5}/> Reset
          </button>
        </div>
        <p className="text-xs text-slate-500 mt-2">
          Tip: upload an image via Emergent&apos;s assets and paste the URL here. Recommended 16:9 aspect (1280×720 or larger).
        </p>
      </section>

      <section className="nb-card p-6 mb-6" data-testid="current-video-card">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="font-display text-2xl font-bold">Marketing video (optional)</h2>
          <button onClick={load} className="nb-btn-secondary text-xs inline-flex items-center gap-1 py-2 px-3"><RefreshCw className="w-3 h-3" strokeWidth={2.5}/> Refresh</button>
        </div>
        {!info || info.status === "none" ? (
          <p className="text-sm text-slate-600 mt-3">No video set. The landing page will show the default poster image.</p>
        ) : info.status === "generating" ? (
          <div className="mt-3 nb-card-sm p-4" style={{ background: "#FDE04733" }}>
            <p className="text-sm font-medium flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.5}/> Generating with Sora 2 — this typically takes 2-5 minutes. Stay on this page or come back.</p>
            {info.prompt && <p className="text-xs text-slate-600 mt-2">Prompt: <em>{info.prompt}</em></p>}
          </div>
        ) : info.status === "failed" ? (
          <div className="mt-3 nb-card-sm p-4 bg-pink-100">
            <p className="text-sm font-medium">Generation failed: {info.error || "unknown error"}</p>
          </div>
        ) : (
          <div className="mt-3">
            <video src={info.video_url} controls className="w-full max-w-2xl rounded-md border-2 border-slate-900 aspect-video bg-black" data-testid="current-video"/>
            <div className="text-xs text-slate-600 mt-2">Source: <strong>{info.source}</strong> · Created {new Date(info.created_at).toLocaleString()}</div>
            <button onClick={remove} data-testid="remove-video-btn" className="nb-btn-danger mt-4 inline-flex items-center gap-2 text-sm"><Trash2 className="w-4 h-4" strokeWidth={2.5}/> Remove</button>
          </div>
        )}
      </section>

      <section className="grid md:grid-cols-2 gap-6">
        <div className="nb-card p-6">
          <h2 className="font-display text-2xl font-bold flex items-center gap-2"><Wand2 className="w-5 h-5" strokeWidth={2.5}/> Generate with AI</h2>
          <p className="text-sm text-slate-600 mt-1">Sora 2 — uses your Universal LLM key.</p>

          <label className="block mt-4 text-xs uppercase font-bold tracking-widest">Prompt</label>
          <textarea data-testid="ai-prompt" rows={5} value={prompt} onChange={(e) => setPrompt(e.target.value)} className="nb-input mt-1"/>

          <div className="grid grid-cols-3 gap-3 mt-3">
            <div>
              <label className="block text-xs uppercase font-bold tracking-widest">Size</label>
              <select data-testid="ai-size" value={size} onChange={(e) => setSize(e.target.value)} className="nb-input mt-1">
                {SIZES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase font-bold tracking-widest">Duration</label>
              <select data-testid="ai-duration" value={duration} onChange={(e) => setDuration(Number(e.target.value))} className="nb-input mt-1">
                {DURATIONS.map((d) => <option key={d} value={d}>{d}s</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase font-bold tracking-widest">Model</label>
              <select data-testid="ai-model" value={model} onChange={(e) => setModel(e.target.value)} className="nb-input mt-1">
                <option value="sora-2">sora-2</option>
                <option value="sora-2-pro">sora-2-pro</option>
              </select>
            </div>
          </div>

          <button onClick={generate} disabled={busy} data-testid="ai-generate-btn" className="nb-btn-primary mt-5 inline-flex items-center gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.5}/> : <Sparkles className="w-4 h-4" strokeWidth={2.5}/>}
            {busy ? "Starting…" : "Generate"}
          </button>
          <p className="text-xs text-slate-500 mt-2">Generation typically takes 2-5 minutes; pro takes longer. We&apos;ll show progress here.</p>
        </div>

        <div className="nb-card p-6">
          <h2 className="font-display text-2xl font-bold flex items-center gap-2"><Upload className="w-5 h-5" strokeWidth={2.5}/> Upload your own</h2>
          <p className="text-sm text-slate-600 mt-1">MP4 / MOV / WebM — max 50 MB.</p>
          <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="upload-video-btn" className="nb-btn-secondary mt-5 inline-flex items-center gap-2">
            <Upload className="w-4 h-4" strokeWidth={2.5}/> Choose file
          </button>
          <input ref={fileRef} type="file" accept="video/*" onChange={upload} className="hidden" data-testid="upload-video-input"/>
        </div>
      </section>
    </div>
  );
}
